import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MobileNav } from '../components/MobileNav';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

const rarityColors = {
  common: { bg: 'rgba(163,184,172,0.2)', border: 'rgba(163,184,172,0.5)', text: '#A3B8AC' },
  rare: { bg: 'rgba(59,130,246,0.2)', border: 'rgba(59,130,246,0.5)', text: '#3B82F6' },
  epic: { bg: 'rgba(168,85,247,0.2)', border: 'rgba(168,85,247,0.5)', text: '#A855F7' },
  legendary: { bg: 'rgba(212,175,55,0.2)', border: 'rgba(212,175,55,0.5)', text: '#D4AF37' }
};

export const DrawHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lucky-draws/history`, { withCredentials: true });
        setHistory(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Draw History</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No draws yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {history.map((h) => {
              const r = rarityColors[h.rarity] || rarityColors.common;
              return (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-4 backdrop-blur-xl flex gap-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`draw-${h.id}`}
                >
                  <img src={h.prize_image} alt={h.prize_name} className="w-20 h-20 rounded-xl object-cover" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23080C0A"/%3E%3C/svg%3E'; }} />
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: '#F8FAF9' }}>{h.prize_name}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: r.bg, border: `1px solid ${r.border}`, color: r.text }}>{h.rarity?.toUpperCase()}</span>
                    <p className="text-xs mt-2" style={{ color: '#A3B8AC' }}>{new Date(h.created_at).toLocaleString('id-ID')}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};
